import { ProductTypeT } from "src/pages/product/product-types";
import { Sort } from "../public-types";

export const CUSTOM_FIELD_TYPE = {
  // Custom Field 기본 타입
  RADIO: "RADIO", // 단일선택
  CHECKBOX: "CHECKBOX", // 멀티선택
  NUMBER: "NUMBER", // 숫자
  TEXT: "TEXT", // 텍스트
  CALENDAR: "CALENDAR", // 캘린더
  URL: "URL", // URL
} as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPE)[keyof typeof CUSTOM_FIELD_TYPE] | "";

// Custom Field 정의
export type CustomFieldModel = {
  fieldId: number; // 필드 ID (신규 등록인 경우, 0 전달, 수정인 경우, 초회 fieldId 재전달)
  partnerId?: string; // 파트너 ID
  fieldName?: string; // 필드명
  fieldType?: CustomFieldType; // 필드 유형 (단일선택: RADIO, 멀티선택: CHECKBOX, 숫자: NUMBER, 텍스트: TEXT, 캘린더: CALENDAR, URL: URL)
  isRequired?: boolean; // 필수 여부 (default = false)
  isVisible?: boolean; // 노출 여부 (default = false)
  orderNum?: number; // 정렬 순번
  isDeleted?: boolean; // 삭제 여부 (default = false)
  createdDate?: string; // 생성일
  createdBy?: string; // 생성자
  modifiedDate?: string; // 수정일
  modifiedBy?: string; // 수정자
  optionList?: CustomFieldOption[]; // 커스텀 필드 옵션 리스트
  groupIdList?: number[]; // 커스텀 필드를 사용중인 계약그룹 ID 리스트
};

// Custom Field 옵션
export type CustomFieldOption = {
  optionId: number; // 옵션 ID (신규 등록인 경우, 0 전달, 수정인 경우, 초회 optionId 재전달)
  optionName: string; // 옵션명
  orderNum?: number; // 정렬 순번
  isDeleted?: boolean; // 삭제 여부 (default = false)
  createdDate?: string; // 생성일
  createdBy?: string; // 생성자
  modifiedDate?: string; // 수정일
  modifiedBy?: string; // 수정자
};

/**
 * 계약그룹 모델 (ctContractGroup)
 * API 명세서 기준으로 정의된 계약그룹 엔티티
 */
export interface ContractGroupModel {
  groupId: number; //계약그룹 ID - 신규 등록 시 0 전달, 수정 시 초회 groupId 재전달
  partnerId?: number; //파트너 ID - CtrlRoom에서 등록시 파트너ID를 Context 값을 사용하므로 전달 불필요, 그외 시스템에서 등록시 파트너 ID 전달 필요
  groupName: string; //그룹명 - 신규 등록 시 입력, 수정/삭제 시 재입력 가능
  isDeleted?: boolean; //삭제 여부 (default = false)
  createdDate?: string; //조회 전용 항목 - 생성일
  createdBy?: string; //조회 전용 항목 - 생성자
  modifiedDate?: string; //조회 전용 항목 - 수정일
  modifiedBy?: string; //조회 전용 항목 - 수정자
  inputList?: ContractGroupInput[]; //계약그룹 입력값 리스트
  groupStatus?: ContractGroupStatus; //계약그룹 상태
}

/**
 * 계약그룹 입력값 모델 (ctContractGroupInput)
 * 계약그룹에 포함된 커스텀 필드의 입력값을 저장하는 엔티티
 */
export interface ContractGroupInput {
  // valueId: number; //입력값 ID - 신규 등록 시 0 전달, 수정 시 초회 valueId 재전달
  fieldId: number; //필드 ID
  inputValue: string; //입력값
  isDeleted?: boolean; //삭제 여부 (default = false)
  optionList?: CustomFieldOption[]; //옵션 리스트
  createdDate?: string; //조회 전용 항목 - 생성일
  createdBy?: string; //조회 전용 항목 - 생성자
  modifiedDate?: string; //조회 전용 항목 - 수정일
  modifiedBy?: string; //조회 전용 항목 - 수정자
}

//  계약 그룹에 배정된 계약 모델 (ctContractGroupContractMapping)
export type AssignedContractModel = {
  contractGroupMap: ContractGroupMap; //계약그룹 매핑 정보
  contractApplyNumber: string; //신청번호
  spaceProductType: string; //상품유형
  contractStep: string; //계약단계
  contractName: string; //계약명
  mbOrganizationId: number; //계약자 ID (계약자 ID)
  spaceProductId: number; //상품ID
  spaceProductName: string; //상품명
  memberNums: number; //현재 이용자 수
  memberMaxNums: number; //최대 이용자 수
  orgName?: string; //계약자명
  contractStartDate: string; //계약시작일
  contractEndDate: string; //계약종료일
};

// 계약과 계약그룹을 연결하는 매핑 테이블
export type ContractGroupMap = {
  mapId: number; //매핑 ID - 신규 등록 시 0 전달, 수정 시 초회 mapId 재전달
  groupId: number; //계약그룹 ID - PathVariable의 계약그룹ID를 사용하여 적용
  contractId: string; //계약 ID
  isDeleted?: boolean; //삭제 여부 (default = false)
  createdDate?: string; //조회 전용 항목 - 생성일
  createdBy?: string; //조회 전용 항목 - 생성자
  modifiedDate?: string; //조회 전용 항목 - 수정일
  modifiedBy?: string; //조회 전용 항목 - 수정자
};

// 계약 그룹에 매핑할 계약 request
export interface ContractGroupMappingRequest {
  // mapId: number;
  groupId: string;
  contractId: string;
  isDeleted?: boolean;
}

export const CONTRACT_GROUP_STATUS = {
  NONE: "NONE", // 비어있음
  PLANNED: "PLANNED", // 이용예정
  ONGOING: "ONGOING", // 이용중
  COMPLETED: "COMPLETED", // 이용종료
} as const;
export type ContractGroupStatus = (typeof CONTRACT_GROUP_STATUS)[keyof typeof CONTRACT_GROUP_STATUS] | "";

// 계약그룹 목록 조회 모델
export interface ContractGroupListModel {
  groupId: number; //계약그룹 ID
  groupName: string; //계약그룹명
  groupStatus: ContractGroupStatus; //계약그룹 상태
  groupStartDate: string; //계약그룹 시작일
  groupEndDate: string; //계약그룹 종료일
  contractCount: number; //계약 개수
  mbOrganizationIdList: number[]; //계약자 ID(=사업자) 리스트
  spaceProductTypeList: ProductTypeT[]; //공간상품타입 리스트
  inputList: ContractGroupInput[]; //계약그룹 입력값 리스트
  createdDate?: string;
  modifiedDate?: string;
  orgDisplayName?: string;
}

// 계약그룹 목록 조회 파라미터
export interface ContractGroupParam {
  page?: number;
  size?: number;
  sort?: Sort;
  groupName?: string; //계약그룹명
  groupStatus?: ContractGroupStatus; //계약그룹 상태
  mbOrganizationId?: number; //계약자 ID(=사업자)
  mbOrganizationName?: string; //계약자명
  spaceProductType?: ProductTypeT; //공간상품타입
  fieldId?: number; //필드 ID
  inputValue?: string; //필드 입력값
  optionIdList?: string; //커스텀필드 선택 옵션 ID
  searchValue?: string; // 전체 검색 입력값
  searchType?: string; // 검색 타입 ("GROUP_ID" 등)
  searchStartAt?: string; // 이용기간 검색 시작일시(ISO)
  searchEndAt?: string; // 이용기간 검색 종료일시(ISO)
}
